'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  motion,
  MotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { PortableText } from 'next-sanity';
import { GroqImage } from '@/helpers/Image';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { cn } from '@/lib/utils';
import AutoScroll from 'embla-carousel-auto-scroll';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';
import { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';
import { PortableTextBlock } from 'sanity';
import { Collapsible } from '@/shadcn/ui/collapsible';
import FAQListNewWebsite from './FAQListNewWebsite';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function FAQSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cards = data?.testimonialsItems ?? [];
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [stackOffsetPx, setStackOffsetPx] = useState(40 * 8);
  const [isMobile, setIsMobile] = useState(false);

  const cardEls = useRef<(HTMLElement | null)[]>([]);
  const [cardHeightsPx, setCardHeightsPx] = useState<number[]>([]);

  if (!data?.testimonialsHeading && !cards.length) return null;
  const ratingLabel =
    cards.length > 0
      ? `${String(currentCardIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`
      : null;
  return (
    <section
      ref={sectionRef}
      className="relative w-ful py-[8rem] lg:py-[15rem]"
    >
      <div className="h-full">
        <div className="mx-auto flex flex-col w-[min(147.5rem,calc(100%-4rem))] items-center justify-between gap-[6rem] lg:gap-[13rem]  mb-[5rem]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center"
            style={{ willChange: 'transform' }}
          >
            {data?.faqChip && (
              <p className="italic text-center mb-[1rem] font-blacker text-[2rem] leading-[1.5] text-black/60">
                {data.faqChip}
              </p>
            )}

            {data?.faqHeading && (
              <div className="w-full richtext prose-em:font-normal prose-headings:text-black  prose-headings:font-[500]">
                <PortableText
                  value={data.faqHeading}
                  components={richTextComponent}
                />
              </div>
            )}

            {data.faqItems && (
              <div className="w-[min(80rem,100%)] mt-[3rem]">
                <FAQListNewWebsite faq={data.faqItems} />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
